import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Users } from "lucide-react";
import OrganizerLeaderboard from "./OrganizerLeaderboard";

const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;
const Stat = ({ label, value, sub, testid, accent }) => (
  <div className={`rounded-xl px-3 py-2 ${accent || "bg-zinc-50"}`} data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    <div className="font-heading text-lg font-bold leading-tight">{value}</div>
    {sub && <div className="text-[11px] text-zinc-500">{sub}</div>}
  </div>
);

// Statistiche → Regali di gruppo (periodo della pagina)
export default function GiftGroupStatsCard({ gg, tooltipStyle, onRefresh }) {
  if (!gg) return null;
  const monthly = (gg.monthly || []).map((m) => ({ ...m, label: new Date(m.month + "-01").toLocaleDateString("it-IT", { month: "short", year: "2-digit" }) }));
  return (
    <div className="card-elev p-5" data-testid="gift-groups-stats-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading font-semibold text-lg">Regali di gruppo</h3>
          <p className="text-xs text-zinc-500">Gift card collettive avviate nel periodo e loro esito</p>
        </div>
        <Users className="h-5 w-5 text-zinc-500" />
      </div>
      {!gg.started ? (
        <div className="text-sm text-zinc-500 py-6 text-center" data-testid="gift-groups-stats-empty">Nessun regalo di gruppo avviato nel periodo</div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 grid grid-cols-2 gap-2 content-start">
            <Stat label="Avviati" value={gg.started} sub={gg.open ? `${gg.open} ancora aperti` : null} testid="gg-stat-started" />
            <Stat label="Obiettivo raggiunto" value={`${gg.target_reached} · ${gg.success_rate}%`} sub={gg.avg_hours_to_target != null ? `in media ${gg.avg_hours_to_target < 48 ? `${gg.avg_hours_to_target} ore` : `${(gg.avg_hours_to_target / 24).toFixed(1)} giorni`}` : null} testid="gg-stat-reached" accent="bg-emerald-50" />
            <Stat label="Importo medio raccolto" value={eur(gg.avg_collected)} sub={`obiettivo medio ${eur(gg.avg_target)}`} testid="gg-stat-avg" accent="bg-amber-50" />
            <Stat label="Totale raccolto" value={eur(gg.total_collected)} sub={`${gg.avg_participants} partecipanti / regalo`} testid="gg-stat-total" />
            <Stat label="Emessi sotto obiettivo" value={gg.issued_below_target} sub="alla scadenza, ≥ 10 €" testid="gg-stat-below" />
            <Stat label="Scaduti / rimborsati" value={`${gg.expired} / ${gg.refunded}`} sub="sotto il minimo" testid="gg-stat-expired" accent={gg.refunded ? "bg-red-50" : undefined} />
          </div>
          <div className="lg:col-span-3 min-h-[220px]">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={220}>
              <BarChart data={monthly} data-testid="chart-gift-groups">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
                <YAxis yAxisId="n" stroke="#64748B" fontSize={11} allowDecimals={false} />
                <YAxis yAxisId="eur" orientation="right" stroke="#64748B" fontSize={11} tickFormatter={(v) => `€${v}`} />
                <Tooltip {...(tooltipStyle || {})} formatter={(v, name) => (name === "Raccolto" ? eur(v) : v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="n" dataKey="started" name="Avviati" fill="#6366F1" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="n" dataKey="target_reached" name="Obiettivo raggiunto" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="eur" dataKey="collected" name="Raccolto" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {gg.started > 0 && <OrganizerLeaderboard rows={gg.top_organizers} onThanked={onRefresh} />}
    </div>
  );
}
